type CircleBinding = { allowed: () => boolean; sync: () => void };
const bindings = new WeakMap<HTMLElement, CircleBinding>();

/** The two scene shells share one circle interaction; a leaf repaint keeps its delegated listeners. */
export function bindEncounterInfoCircle(host: HTMLElement, frame: HTMLIFrameElement, allowed: () => boolean): () => void {
    const existing = bindings.get(host);
    if (existing) { existing.allowed = allowed; existing.sync(); return existing.sync; }
    let open = false;
    const post = (action: 'toggle' | 'close'): void => frame.contentWindow?.postMessage(
        { type: 'ft3d-encounter-info', payload: { action } }, location.origin);
    const binding: CircleBinding = { allowed, sync: () => {
        const circle = host.querySelector<HTMLElement>('.ft-turn-halo');
        if (!circle) return;
        if (binding.allowed()) {
            circle.setAttribute('role', 'button');
            circle.setAttribute('aria-haspopup', 'dialog');
            circle.setAttribute('aria-expanded', String(open));
            if (!circle.hasAttribute('aria-label')) circle.setAttribute('aria-label', 'Encounter information');
        } else {
            for (const name of ['role', 'aria-haspopup', 'aria-expanded']) circle.removeAttribute(name);
            if (circle.getAttribute('aria-label') === 'Encounter information') circle.removeAttribute('aria-label');
        }
    } };
    const activate = (event: MouseEvent | KeyboardEvent): void => {
        if (!binding.allowed() || !(event.target instanceof Element)) return;
        const circle = event.target.closest('.ft-turn-halo');
        if (!circle || !host.contains(circle)) return;
        // The turn-owner camera badge and any End Turn controls retain their own action.
        const nested = event.target.closest('button, a, input, select, textarea, [data-ft-ab-action]');
        if (nested && nested !== circle) return;
        if (event instanceof KeyboardEvent && (event.repeat || (event.key !== 'Enter' && event.key !== ' '))) return;
        event.preventDefault();
        post('toggle');
    };
    host.addEventListener('click', activate);
    host.addEventListener('keydown', activate);
    window.addEventListener('message', event => {
        if (event.origin !== location.origin || event.source !== frame.contentWindow
            || event.data?.type !== 'ft3d-encounter-info-state') return;
        open = event.data.payload?.open === true;
        binding.sync();
    });
    frame.addEventListener('load', () => { open = false; binding.sync(); });
    document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && open && binding.allowed()) post('close');
    });
    bindings.set(host, binding);
    binding.sync();
    return binding.sync;
}
